/**
 * PRP-WEB-1 W3.3 / DD6: a feet-inches input. Shows the current value as `12' 6"`; accepts
 * `12' 6"`, `12'6`, `12.5` and the other forms `parseFeetInches` reads. Applies on Enter or
 * when focus leaves; Escape puts the current value back.
 */
import { useEffect, useId, useState } from 'react';
import { Input } from '../ui/input';
import { formatFeetInches, parseFeetInches } from './feetInches';

export interface FeetInchesFieldProps {
  label: string;
  valueFt: number;
  /** Return a message to show it under the field (the value was refused). */
  onCommit: (ft: number) => string | void;
  onFocusChange?: (focused: boolean) => void;
  disabled?: boolean;
  testId?: string;
}

export function FeetInchesField({ label, valueFt, onCommit, onFocusChange, disabled, testId }: FeetInchesFieldProps) {
  const id = useId();
  const shown = formatFeetInches(valueFt);
  const [text, setText] = useState(shown);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setText(shown); }, [shown]);

  const commit = () => {
    if (text.trim() === shown) { setError(null); return; }
    const ft = parseFeetInches(text);
    if (ft == null || ft <= 0) { setError(`Type feet and inches, like 12' 6" or 12.5`); return; }
    const msg = onCommit(ft);
    if (msg) { setError(msg); return; }
    setError(null);
  };

  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[11.5px] font-[560] text-[var(--tx-lo)]">{label}</label>
      <Input
        id={id}
        data-testid={testId}
        inputMode="decimal"
        autoComplete="off"
        value={text}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => { onFocusChange?.(false); commit(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setText(shown); setError(null); }
        }}
      />
      {error && <p id={`${id}-err`} role="alert" className="mt-1 text-[11.5px] text-[var(--bad)]">{error}</p>}
    </div>
  );
}
